import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Users, Plus, X, CheckCircle, AlertTriangle, Clock, Circle, LayoutGrid, Smartphone, Send, Mail, MessageSquare } from 'lucide-react'
import api from '../../services/api'

const STATUS_CONFIG: Record<string, { label: string; colour: string; bg: string; icon: any }> = {
  completed:   { label: 'Onboarded',    colour: 'var(--green)',  bg: 'var(--green-light)',  icon: CheckCircle },
  in_progress: { label: 'In Progress',  colour: 'var(--accent)', bg: 'var(--accent-light)', icon: Clock },
  overdue:     { label: 'Overdue',      colour: 'var(--red)',    bg: 'var(--red-light)',     icon: AlertTriangle },
  not_started: { label: 'Not Started',  colour: 'var(--text-muted)', bg: 'var(--bg)',       icon: Circle },
}

function pct(done: number, total: number) {
  if (!total) return 0
  return Math.round((done / total) * 100)
}

const ROLE_OPTIONS = [
  { value: 'field_worker', label: 'Field Worker', desc: 'Logs incidents, completes permits, mobile app' },
  { value: 'supervisor',   label: 'Supervisor',   desc: 'Approves permits, views team, lone worker monitor' },
  { value: 'ops_safety',   label: 'Safety Officer', desc: 'Full safety module access, audits, investigations' },
  { value: 'corp_safety',  label: 'Corporate Safety', desc: 'Cross-site reporting and analytics' },
]

function AddWorkerModal({ templates, onSave, onClose }: {
  templates: any[]; onSave: () => void; onClose: () => void
}) {
  const [step, setStep] = useState<'details' | 'access' | 'onboarding'>('details')
  const [workerId, setWorkerId] = useState('')
  const [userCreated, setUserCreated] = useState(false)
  const [setupLink, setSetupLink] = useState('')

  const [form, setForm] = useState({
    first_name: '', last_name: '', job_title: '', department: '',
    employment_type: 'full_time', employee_id: '', phone: '', email: '',
  })
  const [accessForm, setAccessForm] = useState({
    hseq_access: null as boolean | null,
    app_access: null as boolean | null,
    user_role: 'field_worker',
  })
  const [obForm, setObForm] = useState({ template_id: '', start_date: new Date().toISOString().slice(0, 10) })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const STEPS = ['details', 'access', 'onboarding'] as const
  const stepIdx = STEPS.indexOf(step)
  const stepTitles = ['Worker Details', 'System Access', 'Onboarding Checklist']

  const saveWorker = async () => {
    if (!form.first_name || !form.last_name) { setError('First and last name required'); return }
    if (accessForm.hseq_access && !form.email) { setError('Email is required when granting system access'); return }
    setSaving(true); setError('')
    try {
      const payload: any = {
        ...form,
        phone: form.phone || undefined,
        email: form.email || undefined,
      }
      if (accessForm.hseq_access) {
        payload.create_user = true
        payload.user_role = accessForm.user_role
      }
      const { data } = await api.post('/workforce', payload)
      setWorkerId(data.id)
      setUserCreated(!!data.userCreated)
      setSetupLink(data.setupLink || '')
      setStep('onboarding')
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to create worker')
    } finally {
      setSaving(false)
    }
  }

  const assignOnboarding = async () => {
    if (!obForm.template_id || !workerId) { setError('Select a template and start date'); return }
    setSaving(true); setError('')
    try {
      await api.post(`/onboarding/workers/${workerId}/assign`, obForm)
      onSave()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to assign onboarding')
    } finally {
      setSaving(false)
    }
  }

  const field = (key: keyof typeof form, label: string, placeholder: string, type = 'text', span = false) => (
    <div style={{ gridColumn: span ? '1 / -1' : undefined }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{label}</label>
      <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder} style={{ width: '100%', boxSizing: 'border-box' }} />
    </div>
  )

  const accessBtn = (current: boolean | null, val: boolean, label: string, sub: string) => {
    const sel = current === val
    return (
      <button onClick={() => setAccessForm(f => ({ ...f, hseq_access: val }))} style={{
        flex: 1, border: sel ? '2px solid var(--accent)' : '1px solid var(--border)',
        borderRadius: 10, padding: '14px 12px', background: sel ? 'var(--accent-light)' : 'var(--bg)',
        cursor: 'pointer', textAlign: 'left',
      }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: sel ? 'var(--accent)' : 'var(--text)', marginBottom: 3 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>{sub}</div>
      </button>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--white)', borderRadius: 16, boxShadow: 'var(--shadow-lg)',
        width: '100%', maxWidth: 520, padding: 24, display: 'flex', flexDirection: 'column', gap: 18,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{stepTitles[stepIdx]}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Step {stepIdx + 1} of 3</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 6 }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: i <= stepIdx ? 'var(--accent)' : 'var(--border)',
            }} />
          ))}
        </div>

        {/* ── Step 1: Worker Details ── */}
        {step === 'details' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {field('first_name', 'FIRST NAME *', 'Jane')}
              {field('last_name',  'LAST NAME *',  'Smith')}
              {field('job_title',  'JOB TITLE',    'Field Technician')}
              {field('department', 'DEPARTMENT',   'Operations')}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>EMPLOYMENT TYPE</label>
                <select value={form.employment_type} onChange={e => setForm(f => ({ ...f, employment_type: e.target.value }))} style={{ width: '100%' }}>
                  <option value="full_time">Full Time</option>
                  <option value="part_time">Part Time</option>
                  <option value="casual">Casual</option>
                  <option value="contractor">Contractor</option>
                </select>
              </div>
              {field('employee_id', 'EMPLOYEE ID', 'EMP001')}
              {field('phone', 'MOBILE PHONE', '0412 345 678', 'tel')}
              {field('email', 'EMAIL ADDRESS', 'jane.smith@company.com.au', 'email')}
            </div>
            {error && <p style={{ fontSize: 13, color: 'var(--red)', margin: 0 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={() => {
                if (!form.first_name || !form.last_name) { setError('First and last name required'); return }
                setError(''); setStep('access')
              }}>Next: System Access →</button>
            </div>
          </>
        )}

        {/* ── Step 2: System Access ── */}
        {step === 'access' && (
          <>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                Does <strong>{form.first_name} {form.last_name}</strong> need access to HSEQ Intelligence?
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                {accessBtn(accessForm.hseq_access, true,  'Yes — create account', 'They will log in to the platform and/or mobile app')}
                {accessBtn(accessForm.hseq_access, false, 'No — worker only',    'Workforce record only, no login credentials')}
              </div>
            </div>

            {accessForm.hseq_access === true && (
              <>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>ROLE</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {ROLE_OPTIONS.map(r => {
                      const sel = accessForm.user_role === r.value
                      return (
                        <button key={r.value} onClick={() => setAccessForm(f => ({ ...f, user_role: r.value }))} style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          border: sel ? '2px solid var(--accent)' : '1px solid var(--border)',
                          borderRadius: 8, padding: '10px 14px', background: sel ? 'var(--accent-light)' : 'var(--bg)',
                          cursor: 'pointer', textAlign: 'left',
                        }}>
                          <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${sel ? 'var(--accent)' : 'var(--border)'}`, background: sel ? 'var(--accent)' : 'transparent', flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: sel ? 'var(--accent)' : 'var(--text)' }}>{r.label}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.desc}</div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>Is mobile app access required?</p>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {([true, false] as const).map(val => {
                      const sel = accessForm.app_access === val
                      return (
                        <button key={String(val)} onClick={() => setAccessForm(f => ({ ...f, app_access: val }))} style={{
                          flex: 1, border: sel ? '2px solid var(--accent)' : '1px solid var(--border)',
                          borderRadius: 8, padding: '10px 12px', background: sel ? 'var(--accent-light)' : 'var(--bg)',
                          cursor: 'pointer', fontWeight: 600, fontSize: 13,
                          color: sel ? 'var(--accent)' : 'var(--text)',
                        }}>
                          {val ? '📱 Yes — mobile app' : '💻 Web portal only'}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {!form.email && (
                  <div style={{ background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--amber)' }}>
                    ⚠ No email address entered — go back and add one to send account setup instructions.
                  </div>
                )}
                {form.email && (
                  <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--green)' }}>
                    ✓ Setup email will be sent to <strong>{form.email}</strong> with a link to set their password.
                  </div>
                )}
              </>
            )}

            {error && <p style={{ fontSize: 13, color: 'var(--red)', margin: 0 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setError(''); setStep('details') }}>← Back</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={saveWorker}
                disabled={saving || accessForm.hseq_access === null}>
                {saving ? 'Creating…' : 'Next: Assign Checklist →'}
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: Onboarding Checklist ── */}
        {step === 'onboarding' && (
          <>
            {userCreated && (
              <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--green)', marginBottom: 4 }}>✓ User account created</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {setupLink
                    ? <>Share this setup link with <strong>{form.first_name}</strong> — it expires in 72 hours:<br />
                       <a href={setupLink} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>{setupLink}</a>
                       <br /><span style={{ color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>A welcome email was also sent to {form.email} if email is configured.</span>
                      </>
                    : <>A welcome email with a setup link has been sent to <strong>{form.email}</strong>.</>
                  }
                </div>
              </div>
            )}
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>Select an onboarding checklist to assign to {form.first_name}.</p>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>ONBOARDING TEMPLATE</label>
              <select value={obForm.template_id} onChange={e => setObForm(f => ({ ...f, template_id: e.target.value }))} style={{ width: '100%' }}>
                <option value="">— Select template —</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>START DATE</label>
              <input type="date" value={obForm.start_date} onChange={e => setObForm(f => ({ ...f, start_date: e.target.value }))} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            {error && <p style={{ fontSize: 13, color: 'var(--red)', margin: 0 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onSave}>Skip for now</button>
              <button className="btn btn-primary" style={{ flex: 2 }} onClick={assignOnboarding} disabled={saving || !obForm.template_id}>
                {saving ? 'Assigning…' : 'Assign & Finish'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function ResendInviteButton({ workerId, workerName }: { workerId: string; workerName: string }) {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [setupLink, setSetupLink] = useState('')

  const resend = async () => {
    setState('loading')
    try {
      const { data } = await api.post(`/workforce/${workerId}/resend-invite`)
      setSetupLink(data.setupLink || '')
      setState('done')
    } catch {
      setState('error')
    }
  }

  if (state === 'done') {
    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <span style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>Sent</span>
        {setupLink && (
          <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 50, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, width: 320, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Setup link for {workerName}</div>
            <a href={setupLink} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--accent)', wordBreak: 'break-all' }}>{setupLink}</a>
          </div>
        )}
      </div>
    )
  }

  return (
    <button
      className="btn btn-secondary text-xs px-3 py-1.5"
      style={{ minHeight: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}
      onClick={resend}
      disabled={state === 'loading'}
      title="Resend account setup email"
    >
      <Mail size={12} />
      {state === 'loading' ? '…' : state === 'error' ? 'Failed' : 'Resend Invite'}
    </button>
  )
}

function SendAppLinkModal({ onClose }: { onClose: () => void }) {
  const [channel, setChannel] = useState<'both' | 'email' | 'sms'>('both')
  const [recipients, setRecipients] = useState<'all' | 'workers'>('all')
  const [appUrl, setAppUrl] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState('')

  async function send() {
    setSending(true); setError(''); setResult(null)
    try {
      const { data } = await api.post('/notifications/send-app-link', {
        channel,
        recipients,
        ...(appUrl.trim() && { app_url: appUrl.trim() }),
        ...(message.trim() && { message: message.trim() }),
      })
      setResult(data)
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to send — check your SMS/Email configuration in Notifications settings.')
    } finally {
      setSending(false)
    }
  }

  const channelOpts = [
    { value: 'both',  label: 'SMS + Email', icon: Send,         desc: 'Send via both channels' },
    { value: 'email', label: 'Email only',  icon: Mail,         desc: 'Requires SendGrid configured' },
    { value: 'sms',   label: 'SMS only',    icon: MessageSquare, desc: 'Requires Twilio configured' },
  ] as const

  const recipientOpts = [
    { value: 'all',     label: 'All Team Members', desc: 'Every active user in your account' },
    { value: 'workers', label: 'Workers Only',      desc: 'Workers and supervisors — excludes admins' },
  ] as const

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--white)', borderRadius: 16, boxShadow: 'var(--shadow-lg)',
        width: '100%', maxWidth: 520, padding: 24, display: 'flex', flexDirection: 'column', gap: 20,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Smartphone size={18} style={{ color: 'var(--accent)' }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>Send App Download Link</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>Invite your team to download HSEQ Intelligence</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={16} /></button>
        </div>

        {result ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <CheckCircle size={24} style={{ color: 'var(--green)' }} />
            </div>
            <p style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', marginBottom: 8 }}>Sent successfully</p>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              {result.sent.email > 0 && <div>📧 {result.sent.email} email{result.sent.email !== 1 ? 's' : ''} sent</div>}
              {result.sent.sms > 0 && <div>📱 {result.sent.sms} SMS sent</div>}
              {result.failed > 0 && <div style={{ color: 'var(--amber)' }}>⚠ {result.failed} failed (check delivery settings)</div>}
              {result.skipped?.email && <div style={{ color: 'var(--text-muted)' }}>Email skipped — not configured</div>}
              {result.skipped?.sms && <div style={{ color: 'var(--text-muted)' }}>SMS skipped — not configured</div>}
            </div>
            <button onClick={onClose} className="btn btn-primary" style={{ marginTop: 20 }}>Done</button>
          </div>
        ) : (
          <>
            {/* Channel */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>SEND VIA</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {channelOpts.map(opt => {
                  const Icon = opt.icon
                  const sel = channel === opt.value
                  return (
                    <button key={opt.value} onClick={() => setChannel(opt.value)}
                      style={{
                        border: sel ? '2px solid var(--accent)' : '1px solid var(--border)',
                        borderRadius: 8, padding: '10px 8px', background: sel ? 'var(--accent-light)' : 'var(--bg)',
                        cursor: 'pointer', textAlign: 'center',
                      }}>
                      <Icon size={16} style={{ color: sel ? 'var(--accent)' : 'var(--text-muted)', display: 'block', margin: '0 auto 4px' }} />
                      <div style={{ fontSize: 12, fontWeight: 600, color: sel ? 'var(--accent)' : 'var(--text)' }}>{opt.label}</div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Recipients */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>SEND TO</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {recipientOpts.map(opt => {
                  const sel = recipients === opt.value
                  return (
                    <button key={opt.value} onClick={() => setRecipients(opt.value)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        border: sel ? '2px solid var(--accent)' : '1px solid var(--border)',
                        borderRadius: 8, padding: '10px 14px', background: sel ? 'var(--accent-light)' : 'var(--bg)',
                        cursor: 'pointer', textAlign: 'left',
                      }}>
                      <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${sel ? 'var(--accent)' : 'var(--border)'}`, background: sel ? 'var(--accent)' : 'transparent', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: sel ? 'var(--accent)' : 'var(--text)' }}>{opt.label}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{opt.desc}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* App URL */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>APP DOWNLOAD LINK <span style={{ fontWeight: 400 }}>(optional — uses platform default if blank)</span></label>
              <input value={appUrl} onChange={e => setAppUrl(e.target.value)} placeholder="https://play.google.com/store/apps/…" style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>

            {/* Message */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>CUSTOM MESSAGE <span style={{ fontWeight: 400 }}>(optional)</span></label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Add a personalised note to your team…"
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', fontSize: 14, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)' }}
              />
            </div>

            {error && (
              <div style={{ background: 'var(--red-light)', border: '1px solid var(--red)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={send} disabled={sending}>
                <Send size={14} />
                {sending ? 'Sending…' : 'Send App Link'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default function WorkforceList() {
  const [workers, setWorkers] = useState<any[]>([])
  const [templates, setTemplates] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [showAppLink, setShowAppLink] = useState(false)
  const [filter, setFilter] = useState('')

  const load = () => {
    setLoading(true)
    Promise.all([
      api.get('/workforce'),
      api.get('/onboarding/templates').catch(() => ({ data: { data: [] } })),
    ]).then(([w, t]) => {
      setWorkers(w.data.data)
      setTemplates(t.data.data)
    }).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const filtered = workers.filter(w =>
    !filter || `${w.first_name} ${w.last_name} ${w.job_title} ${w.department}`.toLowerCase().includes(filter.toLowerCase())
  )

  const obRed   = workers.filter(w => w.ob_blocking_incomplete > 0).length
  const obAmber = workers.filter(w => w.onboarding_status === 'overdue').length
  const obNone  = workers.filter(w => !w.onboarding_id).length

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Workforce</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>All workers and their onboarding status.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <Link to="/workforce/team" className="btn btn-secondary flex items-center gap-2">
            <LayoutGrid size={15} /> Team View
          </Link>
          <button onClick={() => setShowAppLink(true)} className="btn btn-secondary flex items-center gap-2">
            <Smartphone size={15} /> Send App Link
          </button>
          <button onClick={() => setShowAdd(true)} className="btn btn-primary flex items-center gap-2">
            <Plus size={15} /> Add Worker
          </button>
        </div>
      </div>

      {/* Status summary chips */}
      {!loading && (obRed > 0 || obAmber > 0 || obNone > 0) && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {obRed > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: 'var(--red-light)', border: '1px solid var(--red)' }}>
              <AlertTriangle size={14} style={{ color: 'var(--red)' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)' }}>{obRed} worker{obRed !== 1 ? 's' : ''} with blocking items incomplete</span>
            </div>
          )}
          {obAmber > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: 'var(--amber-light)', border: '1px solid var(--amber)' }}>
              <Clock size={14} style={{ color: 'var(--amber)' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--amber)' }}>{obAmber} overdue</span>
            </div>
          )}
          {obNone > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <Circle size={14} style={{ color: 'var(--text-muted)' }} />
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{obNone} with no onboarding assigned</span>
            </div>
          )}
        </div>
      )}

      <input
        value={filter}
        onChange={e => setFilter(e.target.value)}
        placeholder="Search workers…"
        style={{ width: '100%', boxSizing: 'border-box', maxWidth: 340 }}
      />

      {loading ? (
        <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <Users size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
          <p className="font-semibold mb-2" style={{ color: 'var(--text)' }}>{filter ? 'No workers match your search' : 'No workers yet'}</p>
          {!filter && <button onClick={() => setShowAdd(true)} className="btn btn-primary inline-flex items-center gap-2 mt-2"><Plus size={14} /> Add Worker</button>}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(w => {
            const s = w.onboarding_status
            const cfg = STATUS_CONFIG[s] || STATUS_CONFIG.not_started
            const Icon = cfg.icon
            const pc = pct(w.ob_completed || 0, w.ob_total || 0)
            const hasBlocking = w.ob_blocking_incomplete > 0

            return (
              <div key={w.id} className="card p-4">
                <div className="flex items-center gap-4">
                  <div className="rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ width: 42, height: 42, background: hasBlocking ? 'var(--red)' : 'var(--accent)', fontSize: 14 }}>
                    {w.first_name[0]}{w.last_name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-semibold" style={{ color: 'var(--text)' }}>{w.first_name} {w.last_name}</p>
                      {hasBlocking && (
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'var(--red-light)', color: 'var(--red)', fontWeight: 700 }}>⚠ BLOCKING ITEMS</span>
                      )}
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {[w.job_title, w.department, w.employment_type?.replace('_', ' ')].filter(Boolean).join(' · ')}
                    </p>
                    {w.onboarding_id ? (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{w.template_name} · {pc}% complete</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: cfg.colour }}>
                            <Icon size={12} /> {cfg.label}
                          </span>
                        </div>
                        <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pc}%`, background: hasBlocking ? 'var(--red)' : cfg.colour, borderRadius: 2, transition: 'width 0.3s' }} />
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>No onboarding assigned</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {w.competency_count > 0 && (
                      <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: 'var(--green-light)', color: 'var(--green)', fontWeight: 600 }}>
                        {w.competency_count} competencies
                      </span>
                    )}
                    {w.user_id && (
                      <ResendInviteButton workerId={w.id} workerName={w.first_name} />
                    )}
                    <Link to={`/workforce/${w.id}/onboarding`} className="btn btn-secondary text-xs px-3 py-1.5" style={{ minHeight: 'auto' }}>
                      View Checklist
                    </Link>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {showAdd && (
        <AddWorkerModal
          templates={templates}
          onSave={() => { setShowAdd(false); load() }}
          onClose={() => setShowAdd(false)}
        />
      )}

      {showAppLink && <SendAppLinkModal onClose={() => setShowAppLink(false)} />}
    </div>
  )
}
